import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, FileSpreadsheet } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@repo/ui/src/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@repo/ui/src/components/ui/drawer';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { cn } from '@repo/ui/src/lib/utils';
import { useClass } from '../../hooks/useClasses';
import { useEnrollmentsForSubject } from '../../hooks/useEnrollmentsForSubject';
import { useSubjects } from '../../hooks/useSubjects';
import { useSubscription } from '../../hooks/useSubscription';
import { useUpgradeDialogStore } from '../../stores/upgradeDialogStore';
import { formatVnd } from '../../lib/formatVnd';
import { CreateTab, initialCreateTabState, type CreateTabState } from './addMembers/CreateTab';
import { PickTab, initialPickSelection, type PickSelection } from './addMembers/PickTab';
import { ConfirmTable } from './addMembers/ConfirmTable';
import { ImportWizard } from '../students/import/ImportWizard';
import { buildExistingEnrollmentMap } from './addMembers/existingEnrollment';
import type { DraftMemberRow } from './addMembers/confirmRows';
import type { ImportClassContext } from '../students/import/types';
import { todayHouseIso } from '../../lib/houseToday';

const DAY_LABELS = ['', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

type Tab = 'create' | 'pick' | 'import';

interface AddMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classId: string;
}

export function AddMembersDialog({ open, onOpenChange, classId }: AddMembersDialogProps) {
  const { t } = useTranslation('scheduling');
  const isMobile = useIsMobile();
  const { data: klass } = useClass(open ? classId : null);
  const { data: subjectEnrollments } = useEnrollmentsForSubject(klass?.subject.id ?? null, null);
  const { data: subjects } = useSubjects();
  const { data: subscription, isPending: subscriptionPending, isError: subscriptionError } = useSubscription();

  const [tab, setTab] = useState<Tab>('create');
  const [phase, setPhase] = useState<'tabs' | 'confirm'>('tabs');
  const [confirmDrafts, setConfirmDrafts] = useState<DraftMemberRow[]>([]);
  // Fix round 1 (item 2): Tạo mới's rows and Chọn từ danh sách's selection
  // are lifted HERE, not local `useState` inside each tab — so switching
  // tabs, or going to the confirm table and back via "Quay lại", keeps
  // every typed row and every pick. Only closing the dialog (`reset` below)
  // clears them.
  const [createState, setCreateState] = useState<CreateTabState>(() => initialCreateTabState());
  const [pickSelected, setPickSelected] = useState<PickSelection>(() => initialPickSelection());

  const existingBySubject = useMemo(
    () => buildExistingEnrollmentMap(subjectEnrollments ?? []),
    [subjectEnrollments],
  );

  // §4.4 — three EXPLICIT states (fix wave M13: the two-state collapse this
  // used to be still behaved correctly — pending was never locked — but did
  // it by accident, via `subscription === undefined` covering pending AND
  // error the same way `.plan` would if read straight off a resolved
  // value). Pending/error both render unlocked (never flash the PRO tag at
  // a paying org); only the resolved, allowed case runs the real check.
  const allowsAiImport = subscriptionPending || subscriptionError ? true : (subscription?.plan.allowsAiImport ?? false);

  const reset = () => {
    setTab('create');
    setPhase('tabs');
    setConfirmDrafts([]);
    setCreateState(initialCreateTabState());
    setPickSelected(initialPickSelection());
  };
  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const subject = subjects?.find((s) => s.id === klass?.subject.id);
  const sortedSlots = useMemo(
    () => (klass ? [...klass.slots].sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime)) : []),
    [klass],
  );
  const sameTime = sortedSlots.length > 0 && sortedSlots.every((s) => s.startTime === sortedSlots[0]!.startTime);
  const scheduleLine = sortedSlots.length > 0
    ? `${sortedSlots.map((s) => DAY_LABELS[s.dayOfWeek]).join(' · ')}${sameTime ? ` ${sortedSlots[0]!.startTime}` : ''}`
    : '';
  const defaultRateForSubtitle = klass?.defaults.ratePerSession ?? klass?.defaults.monthlyRate ?? subject?.defaultRate ?? subject?.defaultMonthlyRate ?? null;
  const subtitle = klass
    ? [scheduleLine, defaultRateForSubtitle != null ? t('addMembers.defaultFeeNote', { rate: formatVnd(defaultRateForSubtitle) }) : null]
      .filter(Boolean).join(' · ')
    : '';

  const excelClassContext: ImportClassContext | null = klass ? {
    classId: klass.id,
    className: klass.displayName,
    subjectName: klass.subject.name,
    startDate: todayHouseIso(),
    pricing: {
      pricingType: klass.defaults.pricingType ?? 'PER_SESSION',
      ...(klass.defaults.pricingType === 'MONTHLY_FLAT' ? {} : { billingPeriod: klass.defaults.billingPeriod ?? 'MONTHLY' }),
      ...(klass.defaults.termSize ? { termSize: klass.defaults.termSize } : {}),
      ...(klass.defaults.chargeAbsent != null ? { chargeAbsent: klass.defaults.chargeAbsent } : {}),
    },
    defaultRate: klass.defaults.pricingType === 'MONTHLY_FLAT'
      ? (klass.defaults.monthlyRate ?? subject?.defaultMonthlyRate ?? null)
      : (klass.defaults.ratePerSession ?? subject?.defaultRate ?? null),
  } : null;

  const handleContinue = (drafts: DraftMemberRow[]) => {
    setConfirmDrafts(drafts);
    setPhase('confirm');
  };

  const clickImportTab = () => {
    if (!allowsAiImport) { useUpgradeDialogStore.getState().open('aiImport'); return; }
    setTab('import');
  };

  const title = phase === 'confirm'
    ? t('addMembers.confirmTitle', { class: klass?.displayName ?? '' })
    : t('addMembers.title', { class: klass?.displayName ?? '' });

  // Fix round 1 (item 1): a flex column that hands its active tab/confirm
  // child a bounded `flex-1 min-h-0` region to fill — each of those, in
  // turn, is its own `flex h-full min-h-0 flex-col` with an internal
  // scrollable body and a `shrink-0` pinned footer (CreateTab/PickTab/
  // ConfirmTable). On the 390 Drawer (a real bounded-height ancestor) this
  // actually pins the footer to the bottom of the sheet; inside the desktop
  // Dialog (unbounded height, `overflow-y-auto` on DialogContent itself)
  // `h-full`/`min-h-0` degrade to a no-op and the whole dialog scrolls as
  // one, same as before — no behavior change there.
  const body = klass ? (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {phase === 'tabs' && (
        <div className="shrink-0 flex gap-1 border-b border-surface-200">
          {(['create', 'pick', 'import'] as Tab[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => (key === 'import' ? clickImportTab() : setTab(key))}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 -mb-px text-sm font-medium border-b-2',
                tab === key ? 'border-brand-500 text-brand-700' : 'border-transparent text-surface-500',
              )}
            >
              {key === 'create' && t('addMembers.tabs.create')}
              {key === 'pick' && t('addMembers.tabs.pick')}
              {key === 'import' && (
                <>
                  {t('addMembers.tabs.import')}
                  {allowsAiImport ? (
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                      <Lock className="h-2.5 w-2.5" /> PRO
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0">
        {phase === 'tabs' && tab === 'create' && (
          <CreateTab
            klass={klass}
            existingBySubject={existingBySubject}
            state={createState}
            onStateChange={setCreateState}
            onContinue={handleContinue}
            onCancel={() => handleOpenChange(false)}
          />
        )}
        {phase === 'tabs' && tab === 'pick' && (
          <PickTab
            klass={klass}
            existingBySubject={existingBySubject}
            selected={pickSelected}
            onSelectedChange={setPickSelected}
            onContinue={handleContinue}
            onCancel={() => handleOpenChange(false)}
          />
        )}
        {phase === 'tabs' && tab === 'import' && allowsAiImport && excelClassContext && (
          <div className="h-full min-h-0 overflow-y-auto">
            <ImportWizard open={open} onOpenChange={(o) => { if (!o) handleOpenChange(false); }} classContext={excelClassContext} embedded />
          </div>
        )}
        {phase === 'confirm' && (
          <ConfirmTable klass={klass} drafts={confirmDrafts} onBack={() => setPhase('tabs')} onDone={() => handleOpenChange(false)} />
        )}
      </div>
    </div>
  ) : (
    <div className="py-10 text-center text-sm text-surface-400">{t('addMembers.loading')}</div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent className="mt-0 h-[100dvh] rounded-t-none flex flex-col">
          <DrawerHeader className="text-left shrink-0">
            <DrawerTitle>{title}</DrawerTitle>
            {phase === 'tabs' && subtitle && <p className="m-0 text-xs text-surface-500">{subtitle}</p>}
          </DrawerHeader>
          {/* No `overflow-y-auto`/bottom padding here any more (fix round 1,
              item 1) — each tab/ConfirmTable owns its own scroll region and
              its own safe-area-padded footer now, so this is just the
              bounded-height flex slot they fill. */}
          <div className="flex-1 min-h-0 flex flex-col px-4">
            {body}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {phase === 'tabs' && subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
